import { useEffect, useRef, useState, useMemo } from "react";
import { useOutlet } from "@/contexts/OutletContext";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { toast } from "sonner";
import { Loader2, Search, SlidersHorizontal, ArrowLeft, ChevronRight, ChevronDown, CircleDollarSign, Phone, CalendarDays, EyeClosed, Eye, EyeOff, Users, Check, MoreVertical, Plus, Pencil, Copy, Trash2, FolderPlus, FolderMinus, X, Upload, Archive, ArchiveRestore, Ghost, UserPlus, Repeat2, Settings2, Merge, Sparkles, ArrowUp, ArrowDown, ChevronsUpDown } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { useTerminology } from "@/contexts/TerminologyContext";
import { isAfter, subDays } from "date-fns";
import { cn } from "@/lib/utils";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  type PipelineStage,
  STAGE_META,
  computeVipThreshold,
  classifyPipelineStage,
} from "@/lib/pipelineStage";
import { useContactApprovalCounts } from "@/components/admin_dashboard/ContactApprovalQueue";
import { type ContactGroup } from "@/components/admin_dashboard/ManageGroupsDialog";
import AdminTabActionBar from "@/components/admin_dashboard/AdminTabActionBar";
import { useSidebar } from "@/components/ui/sidebar";
import CreateCustomerDialog from "@/components/admin_dashboard/CreateCustomerDialog";
import { ChannelLabel } from "@/components/admin_dashboard/ChannelLabel";
import {
  createSampleCustomers,
  isDemoSampleCustomer,
  shouldShowSampleCustomers,
} from "@/lib/customerSamples";
import { associateUnifiedContacts } from "@/lib/unifiedContacts";
import {
  contactDisplayName,
  formatPhoneForDisplay,
  primaryIdentity,
  type ContactIdentifier,
} from "@/lib/contactDisplay";
import MergeCustomersDialog, { type MergeCandidate } from "@/components/admin_dashboard/MergeCustomersDialog";
import {
  calculateCustomerMetrics,
  calculateCustomerMetricTrends,
  type CustomerActivityEvent,
} from "@/lib/customerMetrics";
import {
  ATTRIBUTE_SOURCE_ICONS,
  ATTRIBUTE_SOURCE_LABELS,
  CUSTOMER_ATTRIBUTES,
  CUSTOM_ATTRIBUTES_STORAGE_KEY,
  HIDDEN_ATTRIBUTES_STORAGE_KEY,
  loadCustomAttributes,
  loadHiddenAttributes,
  type CustomerAttributeDefinition,
  type CustomerAttributeKey,
  type CustomerAttributeSource,
  type CustomAttributeDefinition,
} from "@/lib/customerAttributes";
import { linkCustomersToGraph } from "@/lib/customerGraphLink";
import { customerInsightsFromProjection, SENTIMENT_META, type CustomerInsights, type CustomerSentiment } from "@/lib/customerInsights";
import { CustomerInsightValue, SentimentBadge } from "@/components/admin_dashboard/CustomerInsightValue";

interface Customer {
  customer_id: string;
  contact_id?: string | null;
  graph_only?: boolean;
  channel_identifiers?: string[];
  /** Main contact detail (phone, @handle, email) per the shared display rule. */
  identity?: string | null;
  short_id?: string | null;
  name: string;
  email: string;
  phone: string | null;
  notes: string | null;
  created_at: string;
  total_order_value: number;
  recent_order_value: number;
  booking_created_at?: string[];
  last_activity_at: string | null;
  preferred_channel: string | null;
  total_bookings: number;
  graph_stage: PipelineStage | null;
  custom_attributes?: Record<string, unknown>;
  insights?: CustomerInsights;
  archived_at?: string | null;
  is_sample?: boolean;
}

type DateJoinedRange = "all" | "7_days" | "30_days" | "90_days";
type LastActivityRange = DateJoinedRange | "never";
type PhoneFilter = "all" | "yes" | "no";

interface CustomerFilters {
  name: string;
  status: PipelineStage | "all";
  preferredChannel: string;
  email: string;
  orderValueMin: string;
  orderValueMax: string;
  bookingsMin: string;
  bookingsMax: string;
  hasPhone: PhoneFilter;
  dateJoined: DateJoinedRange;
  lastActivity: LastActivityRange;
  custom: Record<string, string>;
}

const DEFAULT_CUSTOMER_FILTERS: CustomerFilters = {
  name: "",
  status: "all",
  preferredChannel: "all",
  email: "",
  orderValueMin: "",
  orderValueMax: "",
  bookingsMin: "",
  bookingsMax: "",
  hasPhone: "all",
  dateJoined: "all",
  lastActivity: "all",
  custom: {},
};

type CustomerScope = "active" | "archived";
type CustomerFilterPage = "menu" | CustomerAttributeKey;
type AttributeSettingsPage = "menu" | "visibility" | "edit" | "add";

const DATE_JOINED_OPTIONS: { value: DateJoinedRange; label: string }[] = [
  { value: "all", label: "All time" },
  { value: "7_days", label: "Last 7 days" },
  { value: "30_days", label: "Last 30 days" },
  { value: "90_days", label: "Last 90 days" },
];

const LAST_ACTIVITY_OPTIONS: { value: LastActivityRange; label: string }[] = [
  ...DATE_JOINED_OPTIONS,
  { value: "never", label: "Never" },
];

const PHONE_OPTIONS: { value: PhoneFilter; label: string }[] = [
  { value: "all", label: "All" },
  { value: "yes", label: "Has phone" },
  { value: "no", label: "No phone" },
];

// Header classes mirror the widths in CUSTOMER_ATTRIBUTES.
const INSIGHT_COLUMNS = [
  { key: "interest", label: "Interested in", width: "w-[240px]" },
  { key: "bestTime", label: "Best time", width: "w-[170px]" },
  { key: "sentiment", label: "Sentiment", width: "w-[140px]" },
  { key: "nextStep", label: "Next best step", width: "w-[260px]" },
] as const;

const formatTableDate = (value: string | null) => value
  ? new Intl.DateTimeFormat("en", { day: "2-digit", month: "short", year: "numeric" }).format(new Date(value))
  : "—";

const formatChannel = (value: string | null) => value
  ? value.replace(/[_-]/g, " ").replace(/\b\w/g, (character) => character.toUpperCase())
  : "—";

const MetricSparkline = ({ points, label }: { points: number[]; label: string }) => {
  const width = 84;
  const height = 34;
  const padding = 2;
  const minimum = Math.min(...points);
  const maximum = Math.max(...points);
  const range = maximum - minimum;
  const coordinates = points.map((point, index) => {
    const x = padding + (index / Math.max(points.length - 1, 1)) * (width - padding * 2);
    const y = range === 0
      ? height / 2
      : padding + ((maximum - point) / range) * (height - padding * 2);
    return `${x},${y}`;
  }).join(" ");

  return (
    <svg
      role="img"
      aria-label={`${label} eight-week trend`}
      viewBox={`0 0 ${width} ${height}`}
      className="h-[34px] w-[84px] shrink-0 overflow-visible text-primary"
    >
      <polyline
        points={coordinates}
        fill="none"
        stroke="currentColor"
        strokeWidth="2.5"
        strokeLinecap="round"
        strokeLinejoin="round"
        vectorEffect="non-scaling-stroke"
      />
    </svg>
  );
};

const formatGrowth = (growth: number) => {
  const normalized = Math.abs(growth) < 0.05 ? 0 : growth;
  return `${normalized > 0 ? "+" : ""}${normalized.toLocaleString(undefined, { maximumFractionDigits: 1 })}%`;
};

// Truncated cell text reveals its full value in a tooltip on hover, like the bookings table.
function CellTip({ tip, children }: { tip: React.ReactNode; children: React.ReactNode }) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>{children}</TooltipTrigger>
      <TooltipContent side="top" className="max-w-[320px] whitespace-pre-wrap break-words text-xs">{tip}</TooltipContent>
    </Tooltip>
  );
}

type SortDirection = "asc" | "desc";

// Only columns with an inherent order are sortable. Status and sentiment sort
// by their natural ranking rather than alphabetically.
const STATUS_SORT_ORDER: PipelineStage[] = ["vip", "new", "active", "at-risk", "dormant", "lapsed"];
const SENTIMENT_SORT_ORDER: CustomerSentiment[] = ["positive", "neutral", "frustrated"];

// Click a header to sort ascending, again for descending, a third time to clear.
function SortableHead({ label, sortKey, sort, onSort, className, children }: {
  label?: string;
  sortKey: string;
  sort: { key: string; dir: SortDirection } | null;
  onSort: (key: string) => void;
  className?: string;
  children?: React.ReactNode;
}) {
  const active = sort?.key === sortKey ? sort.dir : null;
  const Icon = active === "desc" ? ArrowDown : active === "asc" ? ArrowUp : ChevronsUpDown;
  return (
    <TableHead
      className={cn(className, "cursor-pointer select-none")}
      aria-sort={active === "asc" ? "ascending" : active === "desc" ? "descending" : "none"}
      onClick={() => onSort(sortKey)}
    >
      <span className="flex items-center justify-between gap-1.5">
        <span className="flex min-w-0 items-center gap-1.5">{children ?? label}</span>
        <Icon aria-hidden className={cn("h-3.5 w-3.5 shrink-0", active ? "text-foreground" : "text-muted-foreground/60")} />
      </span>
    </TableHead>
  );
}

const GeneralCustomers = () => {
  const { businessId } = useAuth();
  const { customer: customerTerm, customers: customerTerms } = useTerminology();
  const { state: sidebarState, isMobile } = useSidebar();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  // Pending-action counts per contact, for the notification badge on each row.
  const approvalCountFor = useContactApprovalCounts(businessId ?? "");
  // Pending AI relationship suggestions per customer. Only pending edges and
  // the people they touch are loaded, so this stays small as the list grows.
  const { data: relationshipSuggestionCounts } = useQuery({
    queryKey: ["customer-relationship-suggestions", businessId, "all"],
    enabled: !!businessId,
    staleTime: 15_000,
    queryFn: async () => {
      const db = supabase as any;
      const { data: edges, error: edgesError } = await db
        .from("customer_relationships")
        .select("source_person_id, target_person_id")
        .eq("business_id", businessId)
        .in("state", ["inferred", "possible"]);
      if (edgesError) throw edgesError;
      const pending = (edges ?? []) as { source_person_id: string; target_person_id: string }[];
      const personIds = [...new Set(pending.flatMap((edge) => [edge.source_person_id, edge.target_person_id]))];
      const counts = new Map<string, number>();
      for (let start = 0; start < personIds.length; start += 200) {
        const { data: people, error: peopleError } = await db
          .from("relationship_people")
          .select("person_id, customer_id")
          .eq("business_id", businessId)
          .in("person_id", personIds.slice(start, start + 200));
        if (peopleError) throw peopleError;
        const customerByPerson = new Map<string, string>(
          ((people ?? []) as { person_id: string; customer_id: string | null }[])
            .filter((person) => person.customer_id)
            .map((person) => [person.person_id, person.customer_id!]),
        );
        for (const edge of pending) {
          for (const personId of [edge.source_person_id, edge.target_person_id]) {
            const customerId = customerByPerson.get(personId);
            if (customerId) counts.set(customerId, (counts.get(customerId) ?? 0) + 1);
          }
        }
      }
      return counts;
    },
  });
  const { activeOutlet, isAllOutlets } = useOutlet();
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchQuery, setSearchQuery] = useState("");
  const [filters, setFilters] = useState<CustomerFilters>(DEFAULT_CUSTOMER_FILTERS);
  const [filterOpen, setFilterOpen] = useState(false);
  const [filterPage, setFilterPage] = useState<CustomerFilterPage>("menu");
  const [attributeSettingsOpen, setAttributeSettingsOpen] = useState(false);
  const [attributeSettingsPage, setAttributeSettingsPage] = useState<AttributeSettingsPage>("menu");
  const [hiddenAttributes, setHiddenAttributes] = useState<Set<CustomerAttributeKey>>(loadHiddenAttributes);
  const [customAttributes, setCustomAttributes] = useState<CustomAttributeDefinition[]>(loadCustomAttributes);
  const [newAttributeSource, setNewAttributeSource] = useState<CustomerAttributeSource>("given");
  const [newAttributeName, setNewAttributeName] = useState("");
  const [newAttributeLogic, setNewAttributeLogic] = useState("");
  const [editingAttributeId, setEditingAttributeId] = useState<string | null>(null);
  const requestedView = searchParams.get("view");
  const [selectedGroup, setSelectedGroup] = useState("all");
  const [customerScope, setCustomerScope] = useState<CustomerScope>("active");
  const [groupOpen, setGroupOpen] = useState(false);
  const [groupEditor, setGroupEditor] = useState<{ mode: "create" | "rename"; group?: ContactGroup } | null>(null);
  const [groupName, setGroupName] = useState("");
  const [groupSaving, setGroupSaving] = useState(false);
  const [selectedCell, setSelectedCell] = useState<string | null>(null);
  const [sort, setSort] = useState<{ key: string; dir: SortDirection } | null>(null);
  const toggleSort = (key: string) => setSort((current) =>
    current?.key !== key ? { key, dir: "asc" } : current.dir === "asc" ? { key, dir: "desc" } : null);
  const [selectedCustomerIds, setSelectedCustomerIds] = useState<Set<string>>(new Set());
  const [bulkActionPending, setBulkActionPending] = useState(false);
  const [mergeOpen, setMergeOpen] = useState(false);
  const [tableHasScrolled, setTableHasScrolled] = useState(false);
  const [createCustomerOpen, setCreateCustomerOpen] = useState(false);

  const activeOutletId = activeOutlet?.outlet_id || null;

  useEffect(() => {
    // Retired views (pipeline, relationships) may still be bookmarked.
    if (!requestedView) return;
    const next = new URLSearchParams(searchParams);
    next.delete("view");
    next.delete("focus");
    setSearchParams(next, { replace: true });
  }, [requestedView, searchParams, setSearchParams]);

  useEffect(() => {
    if (!isAllOutlets) return;
    setGroupEditor(null);
    setSelectedCustomerIds(new Set());
  }, [isAllOutlets]);

  const {
    data: realCustomers = [],
    isLoading: queryLoading,
    isError: customerQueryFailed,
  } = useQuery({
    queryKey: ['customers', businessId, activeOutletId],
    queryFn: async () => {
      if (!businessId) return [];

      const { data: customersData, error: customersError } = await supabase
        .from("customers")
        .select("*")
        .eq("business_id", businessId)
        .order("created_at", { ascending: false });

      if (customersError) {
        toast.error(`Failed to fetch ${customerTerms.toLowerCase()}`);
        throw customersError;
      }

      let outletListingIds: string[] | null = null;
      if (activeOutletId) {
        const { data: outletListings } = await supabase
          .from("listings")
          .select("listing_id")
          .eq("outlet_id", activeOutletId);
        outletListingIds = outletListings?.map((l: any) => l.listing_id) || [];
      }

      let bookingsData: any[] = [];
      if (outletListingIds === null || outletListingIds.length > 0) {
        let bookingsQuery = supabase
          .from("bookings")
          .select("customer_id, customer_email, booking_date, created_at, listings!inner(price, outlet_id, business_id)")
          .eq("listings.business_id", businessId);
        if (outletListingIds !== null) bookingsQuery = bookingsQuery.in("listing_id", outletListingIds);
        const { data } = await bookingsQuery;
        bookingsData = data ?? [];
      }

      const orderTotals: Record<string, number> = {};
      const recentTotals: Record<string, number> = {};
      const lastActivity: Record<string, string> = {};
      const bookingDatesByCustomerId: Record<string, string[]> = {};
      const bookingDatesByEmail: Record<string, string[]> = {};
      const recentCutoff = subDays(new Date(), 60);

      if (bookingsData) {
        for (const booking of bookingsData) {
          const email = booking.customer_email;
          const price = (booking.listings as any)?.price ?? 0;
          orderTotals[email] = (orderTotals[email] || 0) + price;
          if (booking.booking_date && isAfter(new Date(booking.booking_date), recentCutoff)) {
            recentTotals[email] = (recentTotals[email] || 0) + price;
          }
          if (booking.booking_date && (!lastActivity[email] || booking.booking_date > lastActivity[email])) {
            lastActivity[email] = booking.booking_date;
          }
          if (booking.created_at) {
            if (booking.customer_id) {
              (bookingDatesByCustomerId[booking.customer_id] ??= []).push(booking.created_at);
            }
            if (email) {
              (bookingDatesByEmail[email] ??= []).push(booking.created_at);
            }
          }
        }
      }

      // Customers are business-wide. Imported contacts do not have an outlet
      // association until they book, so filtering the rows by outlet bookings
      // made a successful import appear to vanish. Only the booking-derived
      // totals are scoped to the active outlet.
      const filteredCustomersData = customersData || [];

      // Canonical lifecycle comes from the time-aged Customer Graph. Channel
      // identifiers are included because message-only LINE/IG/Telegram users
      // often have neither a primary email nor a phone yet.
      const [
        { data: graphContacts },
        { data: graphProjections },
        { data: channelIdentifiers },
      ] = await Promise.all([
        supabase
          .from("contacts")
          .select("contact_id, primary_email, primary_phone, display_name, created_at, custom_attributes")
          .eq("business_id", businessId)
          .is("tombstoned_at", null)
          // A merged profile lives on inside the one it was merged into.
          .is("merged_into", null),
        // Typed select inference gives up on the ->> aliases.
        (supabase as any)
          .from("contact_projections")
          // Only the summary keys the inferred columns show, not the whole blob.
          .select("contact_id, status, lifetime_value, last_active_at, preferred_channel, total_bookings, total_messages, active_hours, top_listings, synopsis_llm_generated_at, synopsis_schema_version, predicted_next:synopsis_llm_data->>predicted_next, reengagement_angle:synopsis_llm_data->>reengagement_angle, sentiment:synopsis_llm_data->>sentiment")
          .eq("business_id", businessId),
        (supabase as any)
          .from("channel_identifiers")
          .select("contact_id, identifier_value, channel_type, handle, first_seen_at, last_seen_at")
          .eq("business_id", businessId),
      ]);
      const identifiersByContact = new Map<string, ContactIdentifier[]>();
      for (const identifier of (channelIdentifiers ?? []) as Array<ContactIdentifier & { contact_id: string }>) {
        const list = identifiersByContact.get(identifier.contact_id) ?? [];
        list.push(identifier);
        identifiersByContact.set(identifier.contact_id, list);
      }
      const associations = associateUnifiedContacts({
        customers: filteredCustomersData,
        contacts: graphContacts ?? [],
        projections: graphProjections ?? [],
        channelIdentifiers: channelIdentifiers ?? [],
      });

      const enriched: Customer[] = associations.map((association) => {
        const c = association.customer as any;
        const contact = association.contact as any;
        const projection = association.projection as any;
        const rawStage = String(projection?.status ?? "");
        const graphStage: PipelineStage | null = rawStage === "at_risk"
          ? "at-risk"
          : (["new", "active", "dormant", "lapsed", "vip"] as string[]).includes(rawStage)
          ? rawStage as PipelineStage
          : null;
        const identity = contact
          ? primaryIdentity({
            identifiers: identifiersByContact.get(contact.contact_id) ?? [],
            preferredChannel: projection?.preferred_channel ?? null,
            primaryPhone: contact.primary_phone,
            primaryEmail: contact.primary_email,
          })
          : null;
        if (!c) {
          const preferredChannel = projection?.preferred_channel ??
            (channelIdentifiers ?? []).find((identifier: any) =>
              identifier.contact_id === contact.contact_id
            )?.channel_type?.replace(/_(?:user_id|chat_id|psid|e164)$/, "") ?? null;
          return {
            customer_id: `contact:${contact.contact_id}`,
            contact_id: contact.contact_id,
            graph_only: true,
            channel_identifiers: association.channelIdentifiers,
            identity,
            short_id: null,
            name: contactDisplayName(contact.display_name, identity),
            email: contact.primary_email || "",
            phone: contact.primary_phone,
            notes: null,
            created_at: contact.created_at,
            total_order_value: Number(projection?.lifetime_value ?? 0),
            recent_order_value: 0,
            booking_created_at: [],
            last_activity_at: projection?.last_active_at ?? null,
            preferred_channel: preferredChannel,
            total_bookings: Number(projection?.total_bookings ?? 0),
            graph_stage: graphStage,
            custom_attributes: (contact.custom_attributes as Record<string, unknown> | null) ?? {},
            insights: customerInsightsFromProjection(projection),
            archived_at: null,
          };
        }
        // Email is optional on CRM records; never key booking totals on "".
        const email: string = c.email ?? "";
        return {
          ...c,
          email,
          contact_id: contact?.contact_id ?? null,
          graph_only: false,
          channel_identifiers: association.channelIdentifiers,
          identity,
          total_order_value: projection?.lifetime_value != null
            ? Number(projection.lifetime_value)
            : (email && orderTotals[email]) || 0,
          recent_order_value: (email && recentTotals[email]) || 0,
          booking_created_at: bookingDatesByCustomerId[c.customer_id]
            ?? (email ? bookingDatesByEmail[email] : undefined)
            ?? [],
          last_activity_at: projection?.last_active_at ?? (email ? lastActivity[email] : null) ?? null,
          preferred_channel: projection?.preferred_channel ?? null,
          total_bookings: Number(projection?.total_bookings ?? 0),
          graph_stage: graphStage,
          custom_attributes: (contact?.custom_attributes as Record<string, unknown> | null) ?? {},
          insights: customerInsightsFromProjection(projection),
          archived_at: c.archived_at ?? null,
        };
      });

      return enriched.sort(
        (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime(),
      );
    },
    enabled: !!businessId,
    staleTime: 30000,
  });

  // Records added by hand before New customer created contact profiles have
  // none, so they get no status and Analytics can't count them. Link them in
  // the background, each at most once per visit.
  const profileLinkAttempted = useRef(new Set<string>());
  useEffect(() => {
    if (!businessId) return;
    const ids = realCustomers
      .filter((customer) =>
        !customer.graph_only &&
        !customer.contact_id &&
        !customer.archived_at &&
        (customer.email || customer.phone) &&
        !profileLinkAttempted.current.has(`${businessId}:${customer.customer_id}`)
      )
      .slice(0, 50)
      .map((customer) => customer.customer_id);
    if (ids.length === 0) return;
    for (const id of ids) profileLinkAttempted.current.add(`${businessId}:${id}`);
    void linkCustomersToGraph(businessId, ids).then((linked) => {
      if (linked.length > 0) queryClient.invalidateQueries({ queryKey: ["customers", businessId] });
    });
  }, [businessId, realCustomers, queryClient]);

  const {
    data: sampleState,
    isLoading: sampleStateLoading,
    isSuccess: sampleStateLoaded,
  } = useQuery({
    queryKey: ["customer-sample-state", businessId],
    queryFn: async () => {
      if (!businessId) return null;
      const { data, error } = await supabase
        .from("customer_sample_states")
        .select("retired_at")
        .eq("business_id", businessId)
        .maybeSingle();
      if (error) throw error;
      return data;
    },
    enabled: !!businessId,
    staleTime: 30000,
    retry: false,
  });

  const sampleCustomers = useMemo(() => createSampleCustomers(), []);
  const showingSampleCustomers = shouldShowSampleCustomers({
    realCustomerCount: realCustomers.length,
    retiredAt: sampleState?.retired_at,
    stateLoaded: sampleStateLoaded,
    customerQueryFailed,
  });
  const customers: Customer[] = showingSampleCustomers ? sampleCustomers : realCustomers;
  const customerMetrics = useMemo(
    () => calculateCustomerMetrics(customers),
    [customers],
  );
  const { data: customerMetricEvents = [] } = useQuery({
    queryKey: ["customer-metric-events", businessId],
    queryFn: async (): Promise<CustomerActivityEvent[]> => {
      if (!businessId) return [];
      const { data, error } = await supabase
        .from("contact_events")
        .select("contact_id, event_type, occurred_at")
        .eq("business_id", businessId)
        .in("event_type", ["MessageReceived", "BookingCreated"])
        .gte("occurred_at", subDays(new Date(), 90).toISOString())
        .order("occurred_at", { ascending: true });
      if (error) throw error;
      return data ?? [];
    },
    enabled: !!businessId,
    staleTime: 30000,
  });
  const customerMetricTrends = useMemo(
    () => calculateCustomerMetricTrends(customers, customerMetricEvents),
    [customerMetricEvents, customers],
  );

  const canOpenCustomerDetails = (customer: Customer) =>
    !customer.is_sample || isDemoSampleCustomer(customer.customer_id);

  const openCustomerDetails = (customer: Customer) => {
    if (!canOpenCustomerDetails(customer)) return;
    if (customer.graph_only && customer.contact_id) {
      setSearchParams({ tab: "customer-details", contact: customer.contact_id });
      return;
    }
    setSearchParams({
      tab: "customer-details",
      id: customer.customer_id,
      ...(customer.contact_id ? { graph: customer.contact_id } : {}),
    });
  };

  // The server projection is authoritative. The fallback keeps legacy
  // customers usable until they are bridged to the canonical contact graph.
  const stageByCustomer = useMemo(() => {
    const vipThreshold = computeVipThreshold(customers.map((customer) => customer.recent_order_value));
    const map: Record<string, PipelineStage> = {};
    for (const customer of customers) {
      map[customer.customer_id] = customer.graph_stage ?? classifyPipelineStage(customer, vipThreshold);
    }
    return map;
  }, [customers]);

  const preferredChannelOptions = useMemo(
    () => [...new Set(customers.map((customer) => customer.preferred_channel).filter((channel): channel is string => !!channel))]
      .sort((a, b) => formatChannel(a).localeCompare(formatChannel(b))),
    [customers],
  );

  const allAttributeDefinitions = useMemo<CustomerAttributeDefinition[]>(
    () => [
      ...CUSTOMER_ATTRIBUTES,
      ...customAttributes.map((attribute) => ({
        key: `custom:${attribute.id}` as CustomerAttributeKey,
        label: attribute.name,
        source: attribute.source,
        width: 180,
        icon: ATTRIBUTE_SOURCE_ICONS[attribute.source],
      })),
    ],
    [customAttributes],
  );
  const visibleAttributeDefinitions = useMemo(
    () => allAttributeDefinitions.filter((attribute) => !hiddenAttributes.has(attribute.key)),
    [allAttributeDefinitions, hiddenAttributes],
  );
  const visibleCustomAttributeDefinitions = useMemo(
    () => visibleAttributeDefinitions.filter((attribute) => attribute.key.startsWith("custom:")),
    [visibleAttributeDefinitions],
  );
  const filterableAttributeDefinitions = useMemo(
    () => visibleAttributeDefinitions.filter((attribute) => attribute.source !== "ai"),
    [visibleAttributeDefinitions],
  );
  const visibleAttributeCount = visibleAttributeDefinitions.length;
  const visibleTableWidth = visibleAttributeDefinitions.reduce((total, attribute) => total + attribute.width, 0);

  const activeFilterCount = useMemo(() => {
    let count = 0;
    if (filters.name) count++;
    if (filters.status !== "all") count++;
    if (filters.preferredChannel !== "all") count++;
    if (filters.email) count++;
    if (filters.orderValueMin || filters.orderValueMax) count++;
    if (filters.bookingsMin || filters.bookingsMax) count++;
    if (filters.hasPhone !== "all") count++;
    if (filters.dateJoined !== "all") count++;
    if (filters.lastActivity !== "all") count++;
    count += Object.values(filters.custom).filter(Boolean).length;
    return count;
  }, [filters]);

  const filteredCustomers = useMemo(() => {
    let result = customers.filter((customer) =>
      customerScope === "archived" ? !!customer.archived_at : !customer.archived_at
    );

    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      result = result.filter((c) =>
        (c.short_id?.toLowerCase().includes(q)) ||
        c.customer_id.toLowerCase().includes(q) ||
        c.name.toLowerCase().includes(q) ||
        c.email.toLowerCase().includes(q) ||
        (c.phone?.toLowerCase().includes(q)) ||
        (c.identity?.toLowerCase().includes(q))
      );
    }

    if (filters.name.trim()) {
      const query = filters.name.trim().toLowerCase();
      result = result.filter((customer) => customer.name.toLowerCase().includes(query));
    }

    if (filters.status !== "all") {
      result = result.filter((customer) => stageByCustomer[customer.customer_id] === filters.status);
    }

    if (filters.preferredChannel !== "all") {
      result = result.filter((customer) => customer.preferred_channel === filters.preferredChannel);
    }

    if (filters.email.trim()) {
      const query = filters.email.trim().toLowerCase();
      result = result.filter((customer) => customer.email.toLowerCase().includes(query));
    }

    if (filters.orderValueMin || filters.orderValueMax) {
      const min = filters.orderValueMin ? parseFloat(filters.orderValueMin) : 0;
      const max = filters.orderValueMax ? parseFloat(filters.orderValueMax) : Infinity;
      result = result.filter((c) => {
        const val = c.total_order_value;
        return val >= min && val <= max;
      });
    }

    if (filters.hasPhone !== "all") {
      result = result.filter((c) => {
        const hasPhone = !!c.phone && c.phone.trim() !== "";
        return filters.hasPhone === "yes" ? hasPhone : !hasPhone;
      });
    }

    if (filters.bookingsMin || filters.bookingsMax) {
      const min = filters.bookingsMin ? parseFloat(filters.bookingsMin) : 0;
      const max = filters.bookingsMax ? parseFloat(filters.bookingsMax) : Infinity;
      result = result.filter((customer) => customer.total_bookings >= min && customer.total_bookings <= max);
    }

    if (filters.dateJoined !== "all") {
      const now = new Date();
      result = result.filter((c) => {
        const created = new Date(c.created_at);
        switch (filters.dateJoined) {
          case "7_days": return isAfter(created, subDays(now, 7));
          case "30_days": return isAfter(created, subDays(now, 30));
          case "90_days": return isAfter(created, subDays(now, 90));
          default: return true;
        }
      });
    }

    if (filters.lastActivity !== "all") {
      const now = new Date();
      result = result.filter((customer) => {
        if (filters.lastActivity === "never") return !customer.last_activity_at;
        if (!customer.last_activity_at) return false;
        const lastActivity = new Date(customer.last_activity_at);
        switch (filters.lastActivity) {
          case "7_days": return isAfter(lastActivity, subDays(now, 7));
          case "30_days": return isAfter(lastActivity, subDays(now, 30));
          case "90_days": return isAfter(lastActivity, subDays(now, 90));
          default: return true;
        }
      });
    }

    for (const [attributeKey, query] of Object.entries(filters.custom)) {
      if (!query.trim()) continue;
      const attributeId = attributeKey.replace(/^custom:/, "");
      const normalizedQuery = query.trim().toLowerCase();
      result = result.filter((customer) =>
        String(customer.custom_attributes?.[attributeId] ?? "").toLowerCase().includes(normalizedQuery)
      );
    }

    return result;
  }, [customerScope, customers, searchQuery, filters, stageByCustomer]);

  // Mentor-defined groups (e.g. "Cohort 3") are manual and orthogonal to the
  // computed lifecycle status. A contact can belong to several.
  const { data: contactGroups = [] } = useQuery({
    queryKey: ["contact_groups", businessId],
    queryFn: async () => {
      if (!businessId) return [];
      const { data, error } = await supabase
        .from("contact_groups")
        .select("id, name, color")
        .eq("business_id", businessId)
        .order("created_at", { ascending: true });
      if (error) { toast.error("Failed to fetch groups"); throw error; }
      return data as ContactGroup[];
    },
    enabled: !!businessId,
    staleTime: 30000,
  });

  const { data: groupMemberships = [] } = useQuery({
    queryKey: ["contact_group_members", businessId],
    queryFn: async () => {
      if (!businessId) return [];
      const { data, error } = await supabase
        .from("contact_group_members")
        .select("group_id, customer_id")
        .eq("business_id", businessId);
      if (error) { toast.error("Failed to fetch group memberships"); throw error; }
      return data as { group_id: string; customer_id: string }[];
    },
    enabled: !!businessId,
    staleTime: 15000,
  });

  const groupIdsByCustomer = useMemo(() => {
    const map: Record<string, string[]> = {};
    for (const m of groupMemberships) {
      (map[m.customer_id] ??= []).push(m.group_id);
    }
    return map;
  }, [groupMemberships]);

  const groupFilteredCustomers = useMemo(() => {
    const grouped = selectedGroup === "all"
      ? filteredCustomers
      : filteredCustomers.filter((customer) => groupIdsByCustomer[customer.customer_id]?.includes(selectedGroup));
    if (!sort) return grouped;
    const valueOf = (customer: Customer): number | null => {
      switch (sort.key) {
        case "status": return customer.archived_at ? STATUS_SORT_ORDER.length : STATUS_SORT_ORDER.indexOf(stageByCustomer[customer.customer_id]);
        case "sentiment": {
          const sentiment = customer.insights?.sentiment;
          return sentiment ? SENTIMENT_SORT_ORDER.indexOf(sentiment) : null;
        }
        case "bookings": return customer.total_bookings;
        case "orderValue": return customer.total_order_value;
        case "joined": return customer.created_at ? new Date(customer.created_at).getTime() : null;
        case "activity": return customer.last_activity_at ? new Date(customer.last_activity_at).getTime() : null;
        default: return null;
      }
    };
    const factor = sort.dir === "asc" ? 1 : -1;
    return [...grouped].sort((a, b) => {
      const av = valueOf(a);
      const bv = valueOf(b);
      // Empty values stay at the bottom in either direction.
      if (av == null || bv == null) return av == null && bv == null ? 0 : av == null ? 1 : -1;
      return (av - bv) * factor;
    });
  }, [filteredCustomers, groupIdsByCustomer, selectedGroup, sort, stageByCustomer]);

  const visibleCustomerIds = useMemo(
    () => groupFilteredCustomers
      .filter((customer) => !customer.is_sample)
      .map((customer) => customer.customer_id),
    [groupFilteredCustomers],
  );
  const allVisibleSelected = visibleCustomerIds.length > 0 && visibleCustomerIds.every((id) => selectedCustomerIds.has(id));
  const selectedCustomers = useMemo(
    () => customers.filter((customer) => selectedCustomerIds.has(customer.customer_id)),
    [customers, selectedCustomerIds],
  );
  // Groups and archiving are keyed on CRM records; messaging-only profiles
  // (graph_only) can be merged but not grouped or archived yet, so those
  // actions apply to the CRM records in the selection and skip the rest.
  const crmSelectedIds = selectedCustomers
    .filter((customer) => !customer.graph_only)
    .map((customer) => customer.customer_id);
  const skippedProfileOnlyCount = selectedCustomers.length - crmSelectedIds.length;
  const selectionHasNoCrmRecords = selectedCustomers.length > 0 && crmSelectedIds.length === 0;
  const profileOnlyHint = `Groups and archiving work on imported or booked ${customerTerms.toLowerCase()} only`;
  const skippedNote = skippedProfileOnlyCount > 0
    ? { description: `${skippedProfileOnlyCount} messaging-only ${skippedProfileOnlyCount === 1 ? "contact was" : "contacts were"} skipped.` }
    : undefined;
  const mergeCandidates = useMemo((): [MergeCandidate, MergeCandidate] | null => {
    if (selectedCustomers.length !== 2) return null;
    const toCandidate = (customer: Customer): MergeCandidate => ({
      key: customer.customer_id,
      customerId: customer.graph_only ? null : customer.customer_id,
      contactId: customer.contact_id ?? null,
      name: customer.name,
      identity: customer.identity ?? null,
      email: customer.email || null,
      phone: customer.phone ? formatPhoneForDisplay(customer.phone) : null,
      totalBookings: customer.total_bookings,
      createdAt: customer.created_at,
    });
    return [toCandidate(selectedCustomers[0]), toCandidate(selectedCustomers[1])];
  }, [selectedCustomers]);
  const someVisibleSelected = visibleCustomerIds.some((id) => selectedCustomerIds.has(id));

  useEffect(() => {
    const availableIds = new Set(customers.map((customer) => customer.customer_id));
    setSelectedCustomerIds((current) => {
      const next = new Set([...current].filter((id) => availableIds.has(id)));
      return next.size === current.size ? current : next;
    });
  }, [customers]);

  useEffect(() => {
    if (selectedGroup !== "all" && !contactGroups.some((group) => group.id === selectedGroup)) {
      setSelectedGroup("all");
    }
  }, [contactGroups, selectedGroup]);

  const refreshGroups = () => queryClient.invalidateQueries({ queryKey: ["contact_groups", businessId] });

  const updateSelectedGroupMemberships = async (groupId: string, action: "add" | "remove") => {
    if (!businessId || crmSelectedIds.length === 0) return;
    const customerIds = crmSelectedIds;
    setBulkActionPending(true);
    const { error } = action === "add"
      ? await supabase.from("contact_group_members").upsert(
          customerIds.map((customerId) => ({ group_id: groupId, customer_id: customerId, business_id: businessId })),
          { onConflict: "group_id,customer_id", ignoreDuplicates: true },
        )
      : await supabase.from("contact_group_members").delete().eq("group_id", groupId).in("customer_id", customerIds);
    setBulkActionPending(false);
    if (error) {
      toast.error(`Failed to ${action} ${customerTerms.toLowerCase()} ${action === "add" ? "to" : "from"} group: ${error.message}`);
      return;
    }
    const groupName = contactGroups.find((group) => group.id === groupId)?.name ?? "group";
    toast.success(`${action === "add" ? "Added" : "Removed"} ${customerIds.length} ${customerIds.length === 1 ? customerTerm.toLowerCase() : customerTerms.toLowerCase()} ${action === "add" ? "to" : "from"} “${groupName}”`, skippedNote);
    queryClient.invalidateQueries({ queryKey: ["contact_group_members", businessId] });
  };

  const setSelectedCustomersArchived = async (archived: boolean) => {
    if (!businessId || crmSelectedIds.length === 0) return;
    const customerIds = crmSelectedIds;
    setBulkActionPending(true);
    const { data: changed, error } = await supabase.rpc("set_customers_archived", {
      p_business_id: businessId,
      p_customer_ids: customerIds,
      p_archived: archived,
    });
    setBulkActionPending(false);
    if (error) {
      toast.error(`Failed to ${archived ? "archive" : "unarchive"} ${customerTerms.toLowerCase()}: ${error.message}`);
      return;
    }
    const count = Number(changed ?? customerIds.length);
    toast.success(`${archived ? "Archived" : "Unarchived"} ${count} ${count === 1 ? customerTerm.toLowerCase() : customerTerms.toLowerCase()}`, skippedNote);
    setSelectedCustomerIds(new Set());
    queryClient.invalidateQueries({ queryKey: ["customers", businessId] });
    queryClient.invalidateQueries({ queryKey: ["outreach-holds", businessId] });
  };

  const saveGroup = async () => {
    const name = groupName.trim();
    if (!name || !businessId || !groupEditor) return;
    setGroupSaving(true);
    const { error } = groupEditor.mode === "create"
      ? await supabase.from("contact_groups").insert({ business_id: businessId, name, color: "slate" })
      : await supabase.from("contact_groups").update({ name }).eq("id", groupEditor.group!.id);
    setGroupSaving(false);
    if (error) {
      toast.error(error.code === "23505" ? `A group named "${name}" already exists` : `Failed to save group: ${error.message}`);
      return;
    }
    toast.success(groupEditor.mode === "create" ? `Created "${name}"` : `Renamed to "${name}"`);
    setGroupEditor(null);
    setGroupName("");
    refreshGroups();
  };

  const duplicateGroup = async (group: ContactGroup) => {
    if (!businessId) return;
    const existingNames = new Set(contactGroups.map((item) => item.name.toLowerCase()));
    let name = `${group.name} copy`;
    let copyNumber = 2;
    while (existingNames.has(name.toLowerCase())) name = `${group.name} copy ${copyNumber++}`;
    const { error } = await supabase.from("contact_groups").insert({ business_id: businessId, name, color: group.color });
    if (error) {
      toast.error(`Failed to duplicate group: ${error.message}`);
      return;
    }
    toast.success(`Created "${name}"`);
    refreshGroups();
  };

  const deleteGroup = async (group: ContactGroup) => {
    const { error } = await supabase.from("contact_groups").delete().eq("id", group.id);
    if (error) {
      toast.error(`Failed to delete "${group.name}": ${error.message}`);
      return;
    }
    if (selectedGroup === group.id) setSelectedGroup("all");
    toast.success(`Deleted "${group.name}"`);
    refreshGroups();
    queryClient.invalidateQueries({ queryKey: ["contact_group_members", businessId] });
  };

  const clearFilters = () => setFilters(DEFAULT_CUSTOMER_FILTERS);

  const clearFilterForAttribute = (attribute: CustomerAttributeKey) => {
    setFilters((current) => {
      if (attribute.startsWith("custom:")) {
        const custom = { ...current.custom };
        delete custom[attribute];
        return { ...current, custom };
      }
      switch (attribute) {
        case "name": return { ...current, name: "" };
        case "status": return { ...current, status: "all" };
        case "channel": return { ...current, preferredChannel: "all" };
        case "email": return { ...current, email: "" };
        case "phone": return { ...current, hasPhone: "all" };
        case "bookings": return { ...current, bookingsMin: "", bookingsMax: "" };
        case "orderValue": return { ...current, orderValueMin: "", orderValueMax: "" };
        case "joined": return { ...current, dateJoined: "all" };
        case "activity": return { ...current, lastActivity: "all" };
        // Inferred columns have no filters.
        case "interest":
        case "bestTime":
        case "sentiment":
        case "nextStep":
          return current;
      }
    });
  };

  const setAttributeVisible = (attribute: CustomerAttributeKey, visible: boolean) => {
    if (attribute === "name") return;
    setHiddenAttributes((current) => {
      const next = new Set(current);
      if (visible) next.delete(attribute);
      else next.add(attribute);
      window.localStorage.setItem(HIDDEN_ATTRIBUTES_STORAGE_KEY, JSON.stringify([...next]));
      return next;
    });
    if (!visible) clearFilterForAttribute(attribute);
  };

  const showAllAttributes = () => {
    setHiddenAttributes(new Set());
    window.localStorage.setItem(HIDDEN_ATTRIBUTES_STORAGE_KEY, "[]");
  };

  const hideAllAttributes = () => {
    const hidden = new Set<CustomerAttributeKey>(
      allAttributeDefinitions
        .map((attribute) => attribute.key)
        .filter((attribute) => attribute !== "name"),
    );
    setHiddenAttributes(hidden);
    window.localStorage.setItem(HIDDEN_ATTRIBUTES_STORAGE_KEY, JSON.stringify([...hidden]));
    setFilters((current) => ({ ...DEFAULT_CUSTOMER_FILTERS, name: current.name }));
  };

  const orderValueSummary = filters.orderValueMin || filters.orderValueMax
    ? `${filters.orderValueMin ? `$${filters.orderValueMin}` : "Any"} – ${filters.orderValueMax ? `$${filters.orderValueMax}` : "Any"}`
    : "Any";
  const bookingsSummary = filters.bookingsMin || filters.bookingsMax
    ? `${filters.bookingsMin || "Any"} – ${filters.bookingsMax || "Any"}`
    : "Any";
  const phoneSummary = PHONE_OPTIONS.find((option) => option.value === filters.hasPhone)?.label ?? "All";
  const dateJoinedSummary = DATE_JOINED_OPTIONS.find((option) => option.value === filters.dateJoined)?.label ?? "All time";
  const lastActivitySummary = LAST_ACTIVITY_OPTIONS.find((option) => option.value === filters.lastActivity)?.label ?? "All time";
  const filterSummary = (attribute: CustomerAttributeKey) => {
    if (attribute.startsWith("custom:")) return filters.custom[attribute] || "Any";
    switch (attribute) {
      case "name": return filters.name || "Any";
      case "status": return filters.status === "all" ? "All" : STAGE_META[filters.status].label;
      case "channel": return filters.preferredChannel === "all" ? "All" : formatChannel(filters.preferredChannel);
      case "email": return filters.email || "Any";
      case "phone": return phoneSummary;
      case "bookings": return bookingsSummary;
      case "orderValue": return orderValueSummary;
      case "joined": return dateJoinedSummary;
      case "activity": return lastActivitySummary;
      case "interest":
      case "bestTime":
      case "sentiment":
      case "nextStep":
        return "Any";
    }
  };

  const beginAddingAttribute = (source: CustomerAttributeSource) => {
    if (source !== "given") return;
    setEditingAttributeId(null);
    setNewAttributeSource(source);
    setNewAttributeName("");
    setNewAttributeLogic("");
    setAttributeSettingsPage("add");
  };

  const beginEditingAttribute = (attribute: CustomAttributeDefinition) => {
    setEditingAttributeId(attribute.id);
    setNewAttributeSource(attribute.source);
    setNewAttributeName(attribute.name);
    setNewAttributeLogic(attribute.logic);
    setAttributeSettingsPage("add");
  };

  const saveNewAttribute = () => {
    const name = newAttributeName.trim();
    const logic = newAttributeLogic.trim();
    if (!name || (newAttributeSource !== "given" && !logic)) return;
    if (allAttributeDefinitions.some((attribute) =>
      attribute.label.toLowerCase() === name.toLowerCase()
      && attribute.key !== `custom:${editingAttributeId}`
    )) {
      toast.error(`An attribute named “${name}” already exists`);
      return;
    }
    const next = editingAttributeId
      ? customAttributes.map((attribute) => attribute.id === editingAttributeId
          ? { ...attribute, name, logic: newAttributeSource === "given" ? "" : logic }
          : attribute)
      : [
          ...customAttributes,
          {
            id: crypto.randomUUID(),
            name,
            source: newAttributeSource,
            logic: newAttributeSource === "given" ? "" : logic,
          },
        ];
    setCustomAttributes(next);
    window.localStorage.setItem(CUSTOM_ATTRIBUTES_STORAGE_KEY, JSON.stringify(next));
    const action = editingAttributeId ? "Updated" : "Added";
    setEditingAttributeId(null);
    setNewAttributeName("");
    setNewAttributeLogic("");
    setAttributeSettingsPage("edit");
    toast.success(`${action} “${name}”`);
  };

  const deleteCustomAttribute = (attribute: CustomAttributeDefinition) => {
    const attributeKey = `custom:${attribute.id}` as CustomerAttributeKey;
    const next = customAttributes.filter((item) => item.id !== attribute.id);
    setCustomAttributes(next);
    window.localStorage.setItem(CUSTOM_ATTRIBUTES_STORAGE_KEY, JSON.stringify(next));
    setHiddenAttributes((current) => {
      const updated = new Set(current);
      updated.delete(attributeKey);
      window.localStorage.setItem(HIDDEN_ATTRIBUTES_STORAGE_KEY, JSON.stringify([...updated]));
      return updated;
    });
    clearFilterForAttribute(attributeKey);
    if (filterPage === attributeKey) setFilterPage("menu");
    toast.success(`Deleted “${attribute.name}”`);
  };
  const isDefaultFilterSummary = (summary: string) => ["Any", "All", "All time"].includes(summary);
  const selectableCellClass = (customerId: string, column: string, className?: string) => cn(
    "admin-data-table-cell",
    selectedCell === `${customerId}:${column}` && "bg-primary/5 ring-1 ring-inset ring-primary/40",
    className,
  );

  const loading = !!businessId && (queryLoading || (realCustomers.length === 0 && sampleStateLoading));

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-6 h-6 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div>
      <AdminTabActionBar className="gap-2">
          <div className="relative w-full max-w-[300px]">
            <Search className="absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
            <Input
              type="search"
              placeholder={`Search ${customerTerms.toLowerCase()}...`}
              className="admin-toolbar-input pl-8"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
          <Button
            type="button"
            size="sm"
            className="ml-auto h-7 gap-1.5 px-2.5"
            disabled={isAllOutlets || !businessId}
            title={isAllOutlets ? `Select an outlet to create a ${customerTerm.toLowerCase()}` : undefined}
            onClick={() => setCreateCustomerOpen(true)}
          >
            <Plus className="h-3.5 w-3.5" />
            New {customerTerm.toLowerCase()}
          </Button>
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="h-7 gap-1.5 px-2.5"
            disabled={isAllOutlets}
            title={isAllOutlets ? `Select an outlet to import ${customerTerms.toLowerCase()}` : undefined}
            onClick={() => navigate("/?tab=customer-import")}
          >
            <Upload className="h-3.5 w-3.5" />
            Import {customerTerms.toLowerCase()}
          </Button>
      </AdminTabActionBar>
      <section
        aria-label="Customer metrics"
        className="-mx-4 px-4 pb-2 pt-4 md:-mx-6 md:px-6"
      >
        <TooltipProvider delayDuration={150}>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
            {[
              {
                label: "Monthly engaged customers",
                value: customerMetrics.monthlyActive.toLocaleString(),
                description: "Interacted in the past 30 days",
                icon: Users,
                trend: customerMetricTrends.monthlyActive,
                positiveIsGood: true,
              },
              {
                label: "Inactive customers",
                value: customerMetrics.inactive.toLocaleString(),
                description: "No interaction in the past 30 days",
                icon: Ghost,
                trend: customerMetricTrends.inactive,
                positiveIsGood: false,
              },
              {
                label: "New customers",
                value: customerMetrics.newThisMonth.toLocaleString(),
                description: "Added this calendar month",
                icon: UserPlus,
                trend: customerMetricTrends.newThisMonth,
                positiveIsGood: true,
              },
              {
                label: "Repeat rate",
                value: `${customerMetrics.repeatRate.toLocaleString(undefined, { maximumFractionDigits: 1 })}%`,
                description: "Booked customers with 2 or more bookings",
                icon: Repeat2,
                trend: customerMetricTrends.repeatRate,
                positiveIsGood: true,
              },
            ].map((metric) => {
              const Icon = metric.icon;
              return (
                <article
                  key={metric.label}
                  className="rounded-lg border border-border bg-card p-4 shadow-sm"
                >
                  <div className="flex items-center gap-2">
                    <Icon className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden="true" />
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <button
                          type="button"
                          className="cursor-help rounded-sm text-left text-sm font-medium text-muted-foreground decoration-dotted underline-offset-4 hover:text-foreground hover:underline focus-visible:text-foreground focus-visible:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                          aria-label={`${metric.label}: ${metric.description}`}
                        >
                          {metric.label}
                        </button>
                      </TooltipTrigger>
                      <TooltipContent side="top" className="max-w-[280px] text-xs font-normal leading-relaxed">
                        {metric.description}
                      </TooltipContent>
                    </Tooltip>
                  </div>
                  <div className="mt-3 flex items-end justify-between gap-3">
                    <div className="flex min-w-0 items-baseline gap-2">
                      <p className="text-2xl font-semibold tabular-nums text-foreground">
                        {metric.value}
                      </p>
                      <span
                        className={cn(
                          "whitespace-nowrap text-xs font-semibold tabular-nums",
                          metric.trend.growth === 0
                            ? "text-muted-foreground"
                            : (metric.trend.growth > 0) === metric.positiveIsGood
                            ? "text-emerald-600"
                            : "text-rose-600",
                        )}
                      >
                        {formatGrowth(metric.trend.growth)}
                      </span>
                    </div>
                    <MetricSparkline points={metric.trend.points} label={metric.label} />
                  </div>
                </article>
              );
            })}
          </div>
        </TooltipProvider>
      </section>
      <div className="admin-tertiary-bar gap-2">
          <Popover open={groupOpen} onOpenChange={setGroupOpen}>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                size="sm"
                className="admin-toolbar-button w-auto max-w-[180px] justify-start font-normal"
              >
                <Users className="h-3.5 w-3.5 shrink-0" />
                <span className="min-w-0 max-w-[120px] truncate text-left">
                  {customerScope === "archived"
                    ? `Archived ${customerTerms.toLowerCase()}`
                    : selectedGroup === "all"
                    ? `All ${customerTerms.toLowerCase()}`
                    : contactGroups.find((group) => group.id === selectedGroup)?.name ?? `All ${customerTerms.toLowerCase()}`}
                </span>
                <ChevronDown className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-[240px] overflow-hidden p-0" align="start">
              {groupEditor ? (
                <form
                  onSubmit={(event) => {
                    event.preventDefault();
                    saveGroup();
                  }}
                >
                  <button type="button" className="flex w-full items-center gap-2 border-b px-3 py-2 text-sm font-medium hover:bg-muted/60" onClick={() => setGroupEditor(null)}>
                    <ArrowLeft className="h-4 w-4" />
                    {groupEditor.mode === "create" ? "Create new group" : "Rename group"}
                  </button>
                  <div className="space-y-3 p-3">
                    <Input autoFocus value={groupName} onChange={(event) => setGroupName(event.target.value)} placeholder="Group name" className="h-8 text-sm" />
                    <Button type="submit" size="sm" className="h-8 w-full" disabled={!groupName.trim() || groupSaving}>
                      {groupSaving && <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />}
                      {groupEditor.mode === "create" ? "Create group" : "Save name"}
                    </Button>
                  </div>
                </form>
              ) : (
                <>
                  <div className="py-1">
                    {[
                      { id: "all", name: `All ${customerTerms.toLowerCase()}` },
                      ...contactGroups,
                    ].map((group) => {
                      const savedGroup = contactGroups.find((item) => item.id === group.id);
                      return (
                        <div key={group.id} className={cn("flex items-center transition-colors hover:bg-muted/60", customerScope === "active" && selectedGroup === group.id && "bg-muted/60")}>
                          <button
                            type="button"
                            className={cn("flex min-w-0 flex-1 items-center gap-3 px-3 py-1.5 text-left text-sm", customerScope === "active" && selectedGroup === group.id && "font-medium")}
                            onClick={() => {
                              setCustomerScope("active");
                              setSelectedGroup(group.id);
                              setSelectedCustomerIds(new Set());
                              setGroupOpen(false);
                            }}
                          >
                            <span className="flex h-4 w-4 shrink-0 items-center justify-center">
                              {customerScope === "active" && selectedGroup === group.id && <Check className="h-3.5 w-3.5" />}
                            </span>
                            <span className="truncate">{group.name}</span>
                          </button>
                          {savedGroup && !isAllOutlets && (
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <button type="button" aria-label={`Actions for ${group.name}`} className="mr-1 flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-muted-foreground hover:bg-muted hover:text-foreground">
                                  <MoreVertical className="h-4 w-4" />
                                </button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent side="right" align="start" className="w-44">
                                <DropdownMenuItem className="focus:bg-muted/60 focus:text-foreground" onSelect={() => {
                                  setGroupEditor({ mode: "rename", group: savedGroup });
                                  setGroupName(savedGroup.name);
                                  setTimeout(() => setGroupOpen(true), 0);
                                }}>
                                  <Pencil className="mr-2 h-4 w-4" /> Rename
                                </DropdownMenuItem>
                                <DropdownMenuItem className="focus:bg-muted/60 focus:text-foreground" onSelect={() => duplicateGroup(savedGroup)}>
                                  <Copy className="mr-2 h-4 w-4" /> Duplicate
                                </DropdownMenuItem>
                                <DropdownMenuItem className="text-destructive focus:bg-muted/60 focus:text-destructive" onSelect={() => deleteGroup(savedGroup)}>
                                  <Trash2 className="mr-2 h-4 w-4" /> Delete
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          )}
                        </div>
                      );
                    })}
                  </div>
                  <button
                    type="button"
                    className={cn(
                      "flex w-full items-center gap-3 border-t px-3 py-2 text-left text-sm transition-colors hover:bg-muted/60",
                      customerScope === "archived" && "bg-muted/60 font-medium",
                    )}
                    onClick={() => {
                      setCustomerScope("archived");
                      setSelectedGroup("all");
                      setSelectedCustomerIds(new Set());
                      const next = new URLSearchParams(searchParams);
                      next.delete("view");
                      next.delete("focus");
                      setSearchParams(next, { replace: true });
                      setGroupOpen(false);
                    }}
                  >
                    <span className="flex h-4 w-4 shrink-0 items-center justify-center">
                      {customerScope === "archived" ? <Check className="h-3.5 w-3.5" /> : <Archive className="h-3.5 w-3.5 text-muted-foreground" />}
                    </span>
                    Archived {customerTerms.toLowerCase()}
                  </button>
                  {!isAllOutlets && (
                    <button
                      type="button"
                      className="flex w-full items-center gap-3 border-t px-3 py-2 text-left text-sm font-medium transition-colors hover:bg-muted/60"
                      onClick={() => {
                        setGroupEditor({ mode: "create" });
                        setGroupName("");
                      }}
                    >
                      <Plus className="h-4 w-4 text-muted-foreground" />
                      Create new group
                    </button>
                  )}
                </>
              )}
            </PopoverContent>
          </Popover>
          <Popover
            open={filterOpen}
            onOpenChange={(open) => {
              setFilterOpen(open);
              if (!open) setFilterPage("menu");
            }}
          >
            <PopoverTrigger asChild>
              <Button variant="outline" size="sm" className="admin-toolbar-button font-normal">
                <SlidersHorizontal className="h-3.5 w-3.5" />
                Filter
                {activeFilterCount > 0 && (
                  <span className="ml-1 flex h-4 w-4 items-center justify-center rounded-full bg-primary text-[9px] font-semibold text-primary-foreground">
                    {activeFilterCount}
                  </span>
                )}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-[340px] overflow-hidden p-0" align="start">
              {filterPage === "menu" ? (
                <div className="py-1">
                  {activeFilterCount > 0 && (
                    <div className="flex justify-end px-3 py-1">
                      <button onClick={clearFilters} className="text-xs text-primary hover:underline">Clear all</button>
                    </div>
                  )}
                  {filterableAttributeDefinitions.map((attribute) => {
                    const Icon = attribute.icon;
                    const summary = filterSummary(attribute.key);
                    return (
                    <button
                      key={attribute.key}
                      type="button"
                      className="flex w-full items-center gap-3 px-3 py-1.5 text-left text-sm transition-colors hover:bg-muted/60"
                      onClick={() => setFilterPage(attribute.key)}
                    >
                      <Icon className="h-4 w-4 shrink-0 text-muted-foreground" />
                      <span className="font-medium">{attribute.label}</span>
                      <span className={cn("ml-auto max-w-[120px] truncate text-xs", isDefaultFilterSummary(summary) ? "text-muted-foreground" : "font-medium text-foreground")}>{summary}</span>
                      <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" />
                    </button>
                    );
                  })}
                </div>
              ) : (
                <div>
                  <button type="button" className="flex w-full items-center gap-2 border-b px-3 py-2 text-sm font-medium hover:bg-muted/60" onClick={() => setFilterPage("menu")}>
                    <ArrowLeft className="h-4 w-4" />
                    {allAttributeDefinitions.find((attribute) => attribute.key === filterPage)?.label}
                  </button>

                  {filterPage.startsWith("custom:") && (
                    <div className="space-y-3 p-3">
                      <Input
                        type="text"
                        placeholder="Contains value..."
                        className="h-8 text-sm"
                        value={filters.custom[filterPage] ?? ""}
                        onChange={(event) => setFilters((current) => ({
                          ...current,
                          custom: { ...current.custom, [filterPage]: event.target.value },
                        }))}
                        autoFocus
                      />
                      <Button size="sm" className="h-8 w-full" onClick={() => setFilterPage("menu")}>Apply</Button>
                    </div>
                  )}

                  {(filterPage === "name" || filterPage === "email") && (
                    <div className="space-y-3 p-3">
                      <Input
                        type={filterPage === "email" ? "email" : "text"}
                        placeholder={`Contains ${filterPage === "email" ? "email" : "name"}...`}
                        className="h-8 text-sm"
                        value={filterPage === "email" ? filters.email : filters.name}
                        onChange={(event) => setFilters((current) => filterPage === "email"
                          ? { ...current, email: event.target.value }
                          : { ...current, name: event.target.value })}
                        autoFocus
                      />
                      <Button size="sm" className="h-8 w-full" onClick={() => setFilterPage("menu")}>Apply</Button>
                    </div>
                  )}

                  {filterPage === "status" && (
                    <div className="py-1">
                      {(["all", "new", "active", "at-risk", "dormant", "lapsed", "vip"] as const).map((status) => (
                        <button
                          key={status}
                          type="button"
                          className={cn("flex w-full items-center px-3 py-1.5 text-left text-sm hover:bg-muted/60", filters.status === status && "bg-muted font-medium")}
                          onClick={() => { setFilters((current) => ({ ...current, status })); setFilterPage("menu"); }}
                        >
                          {status === "all" ? "All" : STAGE_META[status].label}
                        </button>
                      ))}
                    </div>
                  )}

                  {filterPage === "channel" && (
                    <div className="py-1">
                      {["all", ...preferredChannelOptions].map((channel) => (
                        <button
                          key={channel}
                          type="button"
                          className={cn("flex w-full items-center px-3 py-1.5 text-left text-sm hover:bg-muted/60", filters.preferredChannel === channel && "bg-muted font-medium")}
                          onClick={() => { setFilters((current) => ({ ...current, preferredChannel: channel })); setFilterPage("menu"); }}
                        >
                          {channel === "all" ? "All" : formatChannel(channel)}
                        </button>
                      ))}
                    </div>
                  )}

                  {filterPage === "orderValue" && (
                    <div className="space-y-3 p-3">
                      <p className="text-xs text-muted-foreground">Enter a minimum, maximum, or both.</p>
                      <div className="flex items-center gap-2">
                        <Input type="number" min="0" placeholder="Minimum" className="h-8 text-sm" value={filters.orderValueMin} onChange={(e) => setFilters((prev) => ({ ...prev, orderValueMin: e.target.value }))} />
                        <span className="text-xs text-muted-foreground">to</span>
                        <Input type="number" min="0" placeholder="Maximum" className="h-8 text-sm" value={filters.orderValueMax} onChange={(e) => setFilters((prev) => ({ ...prev, orderValueMax: e.target.value }))} />
                      </div>
                      <Button size="sm" className="h-8 w-full" onClick={() => setFilterPage("menu")}>Apply</Button>
                    </div>
                  )}

                  {filterPage === "bookings" && (
                    <div className="space-y-3 p-3">
                      <p className="text-xs text-muted-foreground">Enter a minimum, maximum, or both.</p>
                      <div className="flex items-center gap-2">
                        <Input type="number" min="0" step="1" placeholder="Minimum" className="h-8 text-sm" value={filters.bookingsMin} onChange={(event) => setFilters((current) => ({ ...current, bookingsMin: event.target.value }))} />
                        <span className="text-xs text-muted-foreground">to</span>
                        <Input type="number" min="0" step="1" placeholder="Maximum" className="h-8 text-sm" value={filters.bookingsMax} onChange={(event) => setFilters((current) => ({ ...current, bookingsMax: event.target.value }))} />
                      </div>
                      <Button size="sm" className="h-8 w-full" onClick={() => setFilterPage("menu")}>Apply</Button>
                    </div>
                  )}

                  {filterPage === "phone" && (
                    <div className="py-1">
                      {PHONE_OPTIONS.map((option) => (
                        <button key={option.value} type="button" className={cn("flex w-full items-center px-3 py-1.5 text-left text-sm hover:bg-muted/60", filters.hasPhone === option.value && "bg-muted font-medium")} onClick={() => { setFilters((prev) => ({ ...prev, hasPhone: option.value })); setFilterPage("menu"); }}>{option.label}</button>
                      ))}
                    </div>
                  )}

                  {filterPage === "joined" && (
                    <div className="py-1">
                      {DATE_JOINED_OPTIONS.map((option) => (
                        <button key={option.value} type="button" className={cn("flex w-full items-center px-3 py-1.5 text-left text-sm hover:bg-muted/60", filters.dateJoined === option.value && "bg-muted font-medium")} onClick={() => { setFilters((prev) => ({ ...prev, dateJoined: option.value })); setFilterPage("menu"); }}>{option.label}</button>
                      ))}
                    </div>
                  )}

                  {filterPage === "activity" && (
                    <div className="py-1">
                      {LAST_ACTIVITY_OPTIONS.map((option) => (
                        <button key={option.value} type="button" className={cn("flex w-full items-center px-3 py-1.5 text-left text-sm hover:bg-muted/60", filters.lastActivity === option.value && "bg-muted font-medium")} onClick={() => { setFilters((current) => ({ ...current, lastActivity: option.value })); setFilterPage("menu"); }}>{option.label}</button>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </PopoverContent>
          </Popover>

          <Popover
            open={attributeSettingsOpen}
            onOpenChange={(open) => {
              setAttributeSettingsOpen(open);
              if (!open) setAttributeSettingsPage("menu");
            }}
          >
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                size="icon"
                className="admin-toolbar-button w-7 px-0"
                aria-label="Attribute settings"
                title="Attribute settings"
              >
                <Settings2 className="h-3.5 w-3.5" />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-[340px] overflow-hidden p-0" align="start">
              {attributeSettingsPage === "menu" && (
                <div className="py-1">
                  <button
                    type="button"
                    className="flex w-full items-center gap-3 px-3 py-1.5 text-left text-sm transition-colors hover:bg-muted/60"
                    onClick={() => setAttributeSettingsPage("edit")}
                  >
                    <Pencil className="h-4 w-4 shrink-0 text-muted-foreground" />
                    <span className="font-medium">Edit attributes</span>
                    <ChevronRight className="ml-auto h-4 w-4 text-muted-foreground" />
                  </button>
                  <button
                    type="button"
                    className="flex w-full items-center gap-3 px-3 py-1.5 text-left text-sm transition-colors hover:bg-muted/60"
                    onClick={() => setAttributeSettingsPage("visibility")}
                  >
                    <EyeClosed className="h-4 w-4 shrink-0 text-muted-foreground" />
                    <span className="font-medium">Attributes visibility</span>
                    <ChevronRight className="ml-auto h-4 w-4 text-muted-foreground" />
                  </button>
                </div>
              )}

              {attributeSettingsPage === "visibility" && (
                <div className="max-h-[420px] overflow-y-auto">
                  <div className="flex items-center border-b">
                    <button
                      type="button"
                      className="flex min-w-0 items-center gap-2 px-3 py-2 text-sm font-medium hover:bg-muted/60"
                      onClick={() => setAttributeSettingsPage("menu")}
                    >
                      <ArrowLeft className="h-4 w-4" />
                      Attributes visibility
                    </button>
                    {hiddenAttributes.size > 0 ? (
                      <button type="button" className="ml-auto px-3 py-2 text-xs font-medium text-primary hover:underline" onClick={showAllAttributes}>
                        Show all
                      </button>
                    ) : (
                      <button type="button" className="ml-auto px-3 py-2 text-xs font-medium text-primary hover:underline" onClick={hideAllAttributes}>
                        Hide all
                      </button>
                    )}
                  </div>
                  {(["given", "computed", "ai"] as const).map((source) => {
                    const attributes = allAttributeDefinitions.filter((attribute) => attribute.source === source);
                    const AttributeSourceIcon = ATTRIBUTE_SOURCE_ICONS[source];
                    return (
                      <div key={source} className={cn("py-1", source !== "given" && "border-t border-border")}>
                        <p className="flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-foreground">
                          <AttributeSourceIcon className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
                          {ATTRIBUTE_SOURCE_LABELS[source]}
                        </p>
                        {attributes.length > 0 ? attributes.map((attribute) => {
                          const visible = !hiddenAttributes.has(attribute.key);
                          const VisibilityIcon = visible ? Eye : EyeOff;
                          return (
                            <button
                              type="button"
                              key={attribute.key}
                              disabled={attribute.key === "name"}
                              aria-pressed={visible}
                              aria-label={`${visible ? "Hide" : "Show"} ${attribute.label}`}
                              onClick={() => setAttributeVisible(attribute.key, !visible)}
                              className={cn(
                                "flex w-full items-center gap-3 px-3 py-1.5 text-left text-sm transition-colors hover:bg-muted/60 disabled:cursor-not-allowed disabled:opacity-60",
                                !visible && "text-muted-foreground",
                              )}
                            >
                              <span className="font-medium">{attribute.label}</span>
                              {attribute.key === "name" && <span className="ml-auto text-xs text-muted-foreground">Required</span>}
                              <VisibilityIcon
                                className={cn("h-4 w-4 shrink-0 text-muted-foreground", attribute.key !== "name" && "ml-auto")}
                                aria-hidden="true"
                              />
                            </button>
                          );
                        }) : (
                          <p className="px-3 py-1.5 text-xs text-muted-foreground">No inferred attributes configured yet.</p>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}

              {attributeSettingsPage === "edit" && (
                <div className="max-h-[520px] overflow-y-auto">
                  <button
                    type="button"
                    className="flex w-full items-center gap-2 border-b px-3 py-2 text-sm font-medium hover:bg-muted/60"
                    onClick={() => setAttributeSettingsPage("menu")}
                  >
                    <ArrowLeft className="h-4 w-4" />
                    Edit attributes
                  </button>
                  {(["given", "computed", "ai"] as const).map((source) => {
                    const attributes = allAttributeDefinitions.filter((attribute) => attribute.source === source);
                    const AttributeSourceIcon = ATTRIBUTE_SOURCE_ICONS[source];
                    return (
                      <div key={source} className={cn("py-1", source !== "given" && "border-t border-border")}>
                        <p className="flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-foreground">
                          <AttributeSourceIcon className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
                          {ATTRIBUTE_SOURCE_LABELS[source]}
                        </p>
                        {attributes.map((attribute) => {
                          const customAttribute = attribute.key.startsWith("custom:")
                            ? customAttributes.find((item) => `custom:${item.id}` === attribute.key)
                            : undefined;
                          return (
                            <div
                              key={attribute.key}
                              className={cn(
                                "flex items-center gap-2 px-3 py-1.5 text-sm font-medium",
                                !customAttribute && "text-muted-foreground",
                              )}
                            >
                              <span className="min-w-0 flex-1 truncate">{attribute.label}</span>
                              {customAttribute && (
                                <>
                                  <button
                                    type="button"
                                    className="rounded p-1 text-muted-foreground hover:bg-muted hover:text-foreground"
                                    aria-label={`Edit ${attribute.label}`}
                                    onClick={() => beginEditingAttribute(customAttribute)}
                                  >
                                    <Pencil className="h-3.5 w-3.5" />
                                  </button>
                                  <button
                                    type="button"
                                    className="rounded p-1 text-muted-foreground hover:bg-destructive/10 hover:text-destructive"
                                    aria-label={`Delete ${attribute.label}`}
                                    onClick={() => deleteCustomAttribute(customAttribute)}
                                  >
                                    <Trash2 className="h-3.5 w-3.5" />
                                  </button>
                                </>
                              )}
                            </div>
                          );
                        })}
                        {source === "given" && (
                          <button
                            type="button"
                            className="flex w-full items-center gap-2 px-3 py-1.5 text-left text-sm font-medium text-primary transition-colors hover:bg-muted/60"
                            onClick={() => beginAddingAttribute(source)}
                          >
                            <Plus className="h-4 w-4" />
                            Add new attribute
                          </button>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}

              {attributeSettingsPage === "add" && (
                <div>
                  <button
                    type="button"
                    className="flex w-full items-center gap-2 border-b px-3 py-2 text-sm font-medium hover:bg-muted/60"
                    onClick={() => setAttributeSettingsPage("edit")}
                  >
                    <ArrowLeft className="h-4 w-4" />
                    Edit attributes
                  </button>
                  <form
                    className="space-y-4 p-3"
                    onSubmit={(event) => {
                      event.preventDefault();
                      saveNewAttribute();
                    }}
                  >
                    <div>
                      <p className="text-sm font-medium">
                        {editingAttributeId ? "Edit" : "Add"} {ATTRIBUTE_SOURCE_LABELS[newAttributeSource].toLowerCase()} attribute
                      </p>
                      <p className="mt-1 text-xs text-muted-foreground">
                        {newAttributeSource === "given"
                          ? "Name the information users can provide for a customer."
                          : "Define what the attribute is and how its value should be determined."}
                      </p>
                    </div>
                    <div className="space-y-1.5">
                      <label htmlFor="new-attribute-name" className="text-xs font-medium">Attribute name</label>
                      <Input
                        id="new-attribute-name"
                        value={newAttributeName}
                        onChange={(event) => setNewAttributeName(event.target.value)}
                        placeholder="e.g. Preferred service"
                        autoFocus
                        required
                      />
                    </div>
                    {newAttributeSource !== "given" && (
                      <div className="space-y-1.5">
                        <label htmlFor="new-attribute-logic" className="text-xs font-medium">Attribute logic</label>
                        <Textarea
                          id="new-attribute-logic"
                          value={newAttributeLogic}
                          onChange={(event) => setNewAttributeLogic(event.target.value)}
                          placeholder="Describe how this attribute should be populated or derived..."
                          rows={4}
                          required
                        />
                      </div>
                    )}
                    <Button
                      type="submit"
                      size="sm"
                      className="w-full"
                      disabled={!newAttributeName.trim() || (newAttributeSource !== "given" && !newAttributeLogic.trim())}
                    >
                      {editingAttributeId ? "Save changes" : "Add attribute"}
                    </Button>
                  </form>
                </div>
              )}
            </PopoverContent>
          </Popover>

      </div>
      {showingSampleCustomers && (
        <div className="border-b border-border bg-sky-50/70 px-4 py-2 text-xs text-sky-900" role="status">
          These 10 sample {customerTerms.toLowerCase()} show how your {customerTerm.toLowerCase()} list works. Open Avery Tan to explore a complete demo profile. Samples are never saved or contacted and disappear permanently when your first real {customerTerm.toLowerCase()} is added or imported.
        </div>
      )}
      {customers.length === 0 ? (
        <p className="text-muted-foreground text-center py-12">{customerTerms} will appear here once they make a booking.</p>
      ) : (
        <div>
          <div className="admin-data-table-shell">
            <div
              className="admin-data-table-scroll w-full overflow-hidden bg-card"
              data-frozen-first-column="true"
              data-scrolled={tableHasScrolled}
              onScrollCapture={(event) => {
                const scrollContainer = event.target as HTMLElement;
                setTableHasScrolled(scrollContainer.scrollLeft > 1);
              }}
            >
          <Table className="admin-data-table" style={{ minWidth: Math.max(visibleTableWidth, 640) }}>
            <TableHeader className="admin-data-table-header">
              <TableRow className="admin-data-table-header-row">
                <TableHead className="sticky left-0 z-40 w-[250px] border-r border-border bg-card px-3">
                  <div className="flex items-center gap-2.5">
                    <Checkbox
                      aria-label={`Select all visible ${customerTerms.toLowerCase()}`}
                      className="border-foreground/60 data-[state=checked]:border-black data-[state=checked]:bg-black data-[state=checked]:text-white data-[state=indeterminate]:border-black data-[state=indeterminate]:bg-black data-[state=indeterminate]:text-white"
                      checked={allVisibleSelected ? true : someVisibleSelected ? "indeterminate" : false}
                      disabled={isAllOutlets || visibleCustomerIds.length === 0}
                      onCheckedChange={(checked) => setSelectedCustomerIds((current) => {
                        const next = new Set(current);
                        visibleCustomerIds.forEach((id) => checked ? next.add(id) : next.delete(id));
                        return next;
                      })}
                    />
                    <span>Name</span>
                  </div>
                </TableHead>
                {!hiddenAttributes.has("status") && <SortableHead sortKey="status" sort={sort} onSort={toggleSort} className="w-[140px] px-3" label="Status" />}
                {!hiddenAttributes.has("channel") && <TableHead className="w-[180px] px-3">Preferred channel</TableHead>}
                {INSIGHT_COLUMNS.filter((column) => !hiddenAttributes.has(column.key)).map((column) => (
                  column.key === "sentiment" ? (
                    <SortableHead key={column.key} sortKey={column.key} sort={sort} onSort={toggleSort} className={cn(column.width, "customer-insight-header px-3")}>
                      <Sparkles aria-hidden className="h-3 w-3 shrink-0" />
                      {column.label}
                    </SortableHead>
                  ) : (
                    <TableHead key={column.key} className={cn(column.width, "customer-insight-header px-3")}>
                      <span className="flex items-center gap-1.5">
                        <Sparkles aria-hidden className="h-3 w-3 shrink-0" />
                        {column.label}
                      </span>
                    </TableHead>
                  )
                ))}
                {!hiddenAttributes.has("email") && <TableHead className="w-[300px] px-3">Email</TableHead>}
                {!hiddenAttributes.has("phone") && <TableHead className="w-[210px] px-3">Phone number</TableHead>}
                {!hiddenAttributes.has("bookings") && <SortableHead sortKey="bookings" sort={sort} onSort={toggleSort} className="w-[130px] px-3" label="Bookings" />}
                {!hiddenAttributes.has("orderValue") && <SortableHead sortKey="orderValue" sort={sort} onSort={toggleSort} className="w-[190px] px-3" label="Total order value" />}
                {!hiddenAttributes.has("joined") && <SortableHead sortKey="joined" sort={sort} onSort={toggleSort} className="w-[180px] px-3" label="Date joined" />}
                {!hiddenAttributes.has("activity") && <SortableHead sortKey="activity" sort={sort} onSort={toggleSort} className="w-[180px] px-3" label="Last activity" />}
                {visibleCustomAttributeDefinitions.map((attribute) => (
                  <TableHead key={attribute.key} className="w-[180px] px-3">{attribute.label}</TableHead>
                ))}
              </TableRow>
            </TableHeader>
            <TooltipProvider delayDuration={200}><TableBody className="admin-data-table-body">
              {groupFilteredCustomers.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={visibleAttributeCount} className="h-24 p-0 text-muted-foreground">
                    <div className="sticky left-0 flex w-[100cqw] justify-center px-4 text-center">
                      No {customerTerms.toLowerCase()} found matching your search or filters.
                    </div>
                  </TableCell>
                </TableRow>
              ) : groupFilteredCustomers.map((customer, rowIndex) => {
                const profileAvailable = canOpenCustomerDetails(customer);
                return (
                  <TableRow
                    key={customer.customer_id}
                    className={cn("group admin-data-table-row", customer.is_sample && "bg-sky-50/20")}
                  >
                  <TableCell
                    role={profileAvailable ? "link" : undefined}
                    tabIndex={profileAvailable ? 0 : -1}
                    aria-label={profileAvailable ? `Open ${customer.name} customer profile` : undefined}
                    onClick={() => openCustomerDetails(customer)}
                    onKeyDown={(event) => {
                      if (event.key === "Enter" || event.key === " ") {
                        event.preventDefault();
                        openCustomerDetails(customer);
                      }
                    }}
                    className={cn(
                      "sticky left-0 z-30 h-9 border-r border-border bg-card py-1 pl-3 pr-10 font-medium transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-inset focus-visible:ring-foreground/20",
                      profileAvailable ? "cursor-pointer group-hover:bg-secondary" : "cursor-default group-hover:bg-secondary",
                    )}
                  >
                    <div className="flex min-w-0 items-center gap-2.5">
                      <Checkbox
                        aria-label={`Select ${customer.name}`}
                        className="border-foreground/60 data-[state=checked]:border-black data-[state=checked]:bg-black data-[state=checked]:text-white"
                        checked={selectedCustomerIds.has(customer.customer_id)}
                        disabled={isAllOutlets || customer.is_sample}
                        onClick={(event) => event.stopPropagation()}
                        onCheckedChange={(checked) => setSelectedCustomerIds((current) => {
                          const next = new Set(current);
                          if (checked) next.add(customer.customer_id);
                          else next.delete(customer.customer_id);
                          return next;
                        })}
                      />
                      <CellTip tip={customer.identity && customer.identity !== customer.name && customer.identity.startsWith("@") ? `${customer.name} ${customer.identity}` : customer.name}>
                      <span className="min-w-0 flex-1 truncate">
                        {/* A contact known only by an @handle has no real name yet,
                            so the handle is shown muted like the one beside a name. */}
                        {customer.identity === customer.name && customer.name.startsWith("@") ? (
                          <span className="font-normal text-muted-foreground">{customer.name}</span>
                        ) : (
                          customer.name
                        )}
                        {/* Phone and email have their own columns; show an
                            @handle (Instagram/Telegram) next to the name. */}
                        {customer.identity && customer.identity !== customer.name && customer.identity.startsWith("@") && (
                          <span className="ml-1.5 font-normal text-muted-foreground">{customer.identity}</span>
                        )}
                      </span>
                      </CellTip>
                    </div>
                    {(() => {
                      // One badge for everything awaiting review on this contact:
                      // pending actions plus AI relationship suggestions.
                      const approvals = customer.is_sample ? 0 : approvalCountFor(customer);
                      const suggestions = customer.is_sample ? 0 : relationshipSuggestionCounts?.get(customer.customer_id) ?? 0;
                      const count = approvals + suggestions;
                      if (count === 0) return null;
                      const parts = [
                        approvals > 0 ? `${approvals} action${approvals === 1 ? "" : "s"} awaiting approval` : null,
                        suggestions > 0 ? `${suggestions} relationship suggestion${suggestions === 1 ? "" : "s"} to review` : null,
                      ].filter(Boolean);
                      return (
                        <span
                          title={parts.join(", ")}
                          className="absolute right-3 top-1/2 inline-flex h-5 min-w-[20px] -translate-y-1/2 items-center justify-center rounded-full bg-red-500 px-1.5 text-[10px] font-semibold text-white"
                        >
                          {count}
                        </span>
                      );
                    })()}
                  </TableCell>
                  {!hiddenAttributes.has("status") && <TableCell onClick={() => setSelectedCell(`${customer.customer_id}:status`)} className={selectableCellClass(customer.customer_id, "status")}>
                    {customer.archived_at ? (
                      <span className="inline-flex items-center rounded-full border border-slate-300 bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-700">
                        Archived
                      </span>
                    ) : (() => {
                      const stage = stageByCustomer[customer.customer_id];
                      const meta = STAGE_META[stage];
                      return (
                        <span
                          className={cn(
                            "inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-medium",
                            meta.badge,
                          )}
                        >
                          {meta.label}
                        </span>
                      );
                    })()}
                  </TableCell>}
                  {!hiddenAttributes.has("channel") && <TableCell onClick={() => setSelectedCell(`${customer.customer_id}:channel`)} className={selectableCellClass(customer.customer_id, "channel")}><ChannelLabel channel={customer.preferred_channel} /></TableCell>}
                  {INSIGHT_COLUMNS.filter((column) => !hiddenAttributes.has(column.key)).map((column, columnIndex) => {
                    const sentiment = column.key === "sentiment" ? customer.insights?.sentiment ?? null : null;
                    const text = column.key === "sentiment"
                      ? sentiment && SENTIMENT_META[sentiment].label
                      : customer.insights?.[column.key];
                    return (
                      <TableCell
                        key={column.key}
                        onClick={() => setSelectedCell(`${customer.customer_id}:${column.key}`)}
                        className={selectableCellClass(customer.customer_id, column.key, "customer-insight-cell")}
                      >
                        {text ? (
                          <CellTip tip={text}>
                            <div className="min-w-0">
                              <CustomerInsightValue
                                revealKey={`${customer.customer_id}:${column.key}:${text}`}
                                rowIndex={rowIndex}
                                columnIndex={columnIndex}
                              >
                                {sentiment ? <SentimentBadge sentiment={sentiment} /> : text}
                              </CustomerInsightValue>
                            </div>
                          </CellTip>
                        ) : (
                          <span className="text-muted-foreground">—</span>
                        )}
                      </TableCell>
                    );
                  })}
                  {!hiddenAttributes.has("email") && <TableCell onClick={() => setSelectedCell(`${customer.customer_id}:email`)} className={selectableCellClass(customer.customer_id, "email", "truncate")}>{customer.email ? <CellTip tip={customer.email}><span className="block truncate">{customer.email}</span></CellTip> : "—"}</TableCell>}
                  {!hiddenAttributes.has("phone") && <TableCell onClick={() => setSelectedCell(`${customer.customer_id}:phone`)} className={selectableCellClass(customer.customer_id, "phone")}>{customer.phone || "—"}</TableCell>}
                  {!hiddenAttributes.has("bookings") && <TableCell onClick={() => setSelectedCell(`${customer.customer_id}:bookings`)} className={selectableCellClass(customer.customer_id, "bookings", "text-right tabular-nums")}>{customer.total_bookings}</TableCell>}
                  {!hiddenAttributes.has("orderValue") && <TableCell onClick={() => setSelectedCell(`${customer.customer_id}:orderValue`)} className={selectableCellClass(customer.customer_id, "orderValue", "font-medium tabular-nums")}>${customer.total_order_value.toFixed(2)}</TableCell>}
                  {!hiddenAttributes.has("joined") && <TableCell onClick={() => setSelectedCell(`${customer.customer_id}:joined`)} className={selectableCellClass(customer.customer_id, "joined")}>{formatTableDate(customer.created_at)}</TableCell>}
                  {!hiddenAttributes.has("activity") && <TableCell onClick={() => setSelectedCell(`${customer.customer_id}:activity`)} className={selectableCellClass(customer.customer_id, "activity")}>{formatTableDate(customer.last_activity_at)}</TableCell>}
                  {visibleCustomAttributeDefinitions.map((attribute) => {
                    const attributeId = attribute.key.replace(/^custom:/, "");
                    const value = customer.custom_attributes?.[attributeId];
                    const displayValue = value == null || value === ""
                      ? "—"
                      : typeof value === "object"
                        ? JSON.stringify(value)
                        : String(value);
                    return (
                      <TableCell
                        key={attribute.key}
                        onClick={() => setSelectedCell(`${customer.customer_id}:${attribute.key}`)}
                        className={selectableCellClass(customer.customer_id, attribute.key, "max-w-[180px] truncate")}
                      >
                        {displayValue === "—" ? displayValue : <CellTip tip={displayValue}><span className="block truncate">{displayValue}</span></CellTip>}
                      </TableCell>
                    );
                  })}
                  </TableRow>
                );
              })}
            </TableBody></TooltipProvider>
          </Table>
            </div>
          </div>
        </div>
      )}
      {selectedCustomerIds.size > 0 && (
        <div
          className="pointer-events-none fixed bottom-5 right-0 z-50 flex justify-center"
          style={{ left: isMobile ? 0 : sidebarState === "expanded" ? "var(--sidebar-width)" : "var(--sidebar-width-icon)" }}
        >
        <div className="pointer-events-auto flex items-center gap-1 rounded-lg border border-border bg-background p-1.5 shadow-lg">
          <span className="whitespace-nowrap px-2 text-sm font-medium">{selectedCustomerIds.size} selected</span>
          <div className="mx-1 h-5 w-px bg-border" />
          {mergeCandidates && (
            <Button
              variant="outline"
              size="sm"
              className="admin-toolbar-button font-normal"
              disabled={bulkActionPending}
              onClick={() => setMergeOpen(true)}
            >
              <Merge className="h-4 w-4" />
              Merge
            </Button>
          )}
          {(["add", "remove"] as const).map((action) => (
            <DropdownMenu key={action}>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="outline"
                  size="sm"
                  className="admin-toolbar-button font-normal"
                  disabled={bulkActionPending || selectionHasNoCrmRecords}
                  title={selectionHasNoCrmRecords ? profileOnlyHint : undefined}
                >
                  {action === "add" ? <FolderPlus className="h-4 w-4" /> : <FolderMinus className="h-4 w-4" />}
                  {action === "add" ? "Add to group" : "Remove from group"}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="w-48">
                {contactGroups.length === 0 ? (
                  <p className="px-2 py-1.5 text-xs text-muted-foreground">No groups yet</p>
                ) : contactGroups.map((group) => (
                  <DropdownMenuItem key={group.id} onSelect={() => updateSelectedGroupMemberships(group.id, action)}>
                    {group.name}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          ))}
          <Button
            variant="outline"
            size="sm"
            className="admin-toolbar-button font-normal"
            disabled={bulkActionPending || selectionHasNoCrmRecords}
            title={selectionHasNoCrmRecords ? profileOnlyHint : undefined}
            onClick={() => setSelectedCustomersArchived(customerScope === "active")}
          >
            {customerScope === "active" ? <Archive className="h-4 w-4" /> : <ArchiveRestore className="h-4 w-4" />}
            {bulkActionPending ? (customerScope === "active" ? "Archiving…" : "Unarchiving…") : (customerScope === "active" ? "Archive" : "Unarchive")}
          </Button>
          <Button variant="ghost" size="icon" className="h-8 w-8" aria-label="Clear selection" onClick={() => setSelectedCustomerIds(new Set())}>
            <X className="h-4 w-4" />
          </Button>
        </div>
        </div>
      )}
      {businessId && (
        <MergeCustomersDialog
          open={mergeOpen}
          onOpenChange={setMergeOpen}
          businessId={businessId}
          candidates={mergeCandidates}
          customerTerm={customerTerm}
          onMerged={() => {
            setSelectedCustomerIds(new Set());
            queryClient.invalidateQueries({ queryKey: ["customers", businessId] });
            queryClient.invalidateQueries({ queryKey: ["contact_group_members", businessId] });
            queryClient.invalidateQueries({ queryKey: ["customer-metric-events", businessId] });
          }}
        />
      )}
      {businessId && (
        <CreateCustomerDialog
          open={createCustomerOpen}
          onOpenChange={setCreateCustomerOpen}
          businessId={businessId}
          customerTerm={customerTerm}
          onCreated={() => {
            queryClient.invalidateQueries({ queryKey: ["customers", businessId] });
            queryClient.invalidateQueries({ queryKey: ["customer-sample-state", businessId] });
          }}
        />
      )}
    </div>
  );
};

export default GeneralCustomers;
